import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingApi, tripsApi, podsApi, type Stop, type PodDocument } from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { PortalLayout } from '@/components/Layout';
import { PortalNav } from '@/components/Nav';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmModal } from '@/components/Modal';
import { useToast } from '@/components/Feedback';
import { formatPeso, formatDate, formatDateTime, stopTypeLabel, clientStatusLabel } from '@/lib/format';

const MILESTONE_STATUSES = ['requested', 'confirmed', 'dispatched', 'executing', 'completed'];
/** Internal post-delivery (billing) states the client should see as "Delivered". */
const DELIVERED_STATUSES = ['completed', 'ready_to_bill', 'billed', 'closed'];
/** Trips that ended without delivery — no progress timeline makes sense. */
const ENDED_STATUSES = ['cancelled', 'declined', 'failed'];
/** Statuses a client may cancel from (API also allows later stages, but those need ops). */
const CLIENT_CANCELLABLE_STATUSES = ['requested', 'confirmed', 'on_hold'];

const MILESTONE_LABELS: Record<string, string> = {
  requested: 'Requested',
  confirmed: 'Confirmed',
  dispatched: 'Truck assigned',
  executing: 'In transit',
  completed: 'Delivered',
};

const WHAT_HAPPENS_NEXT: Record<string, string> = {
  requested: 'We\'re reviewing your booking and will confirm it shortly.',
  quote_required: 'We\'re preparing a quote for this trip.',
  confirmed: 'Your trip is confirmed. We\'ll notify you when a truck is assigned.',
  dispatched: 'A truck has been assigned and is heading to pickup.',
  executing: 'Your cargo is on the way. Stop updates appear below as they happen.',
  completed: 'Delivered. Proof of delivery is listed below once available.',
  ready_to_bill: 'Delivered. Proof of delivery is listed below once available.',
  billed: 'Delivered. Proof of delivery is listed below once available.',
  closed: 'Delivered. Proof of delivery is listed below once available.',
  on_hold: 'On hold — our team will contact you.',
  cancelled: 'This trip was cancelled. No further action is needed.',
  declined: 'We couldn\'t take this booking. Please contact your account manager for options.',
  failed: 'This delivery couldn\'t be completed. Our team will contact you about next steps.',
};

type CancelQuote =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'free' }
  | { kind: 'fee'; amount: string; reason: string };

/** Explains the fee in plain words using the notice bands (≥24h free, ≥12h 50%, else 100%). */
function feeReason(hoursNotice: number | null, afterPickup: boolean, chargePct: number): string {
  if (afterPickup || (hoursNotice != null && hoursNotice < 0)) {
    return 'because the scheduled pickup time has passed';
  }
  return `because pickup is less than ${chargePct >= 100 ? 12 : 24} hours away`;
}

export function PortalTripDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canSeeAmounts = hasPermission('portal.book') || hasPermission('portal.view_trips');
  const canCancelTrip = hasPermission('portal.cancel');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [cancelQuote, setCancelQuote] = useState<CancelQuote>({ kind: 'loading' });

  const { data: trip, loading, error, offline, reload } = useAsync(() => tripsApi.get(id!), [id]);
  const { data: pods } = useAsync(() => podsApi.list(id!), [id]);
  const [downloadingPod, setDownloadingPod] = useState<string | null>(null);

  const downloadPod = async (pod: PodDocument) => {
    setDownloadingPod(pod.id);
    try {
      await podsApi.downloadPdf(pod.id, pod.podNumber);
    } catch (err) {
      toast((err as { message?: string }).message ?? 'The PDF could not be downloaded. Please try again.', 'error');
    } finally {
      setDownloadingPod(null);
    }
  };

  useEffect(() => {
    if (!showCancel || !id || !trip) return;
    let cancelled = false;
    setCancelQuote({ kind: 'loading' });
    setCancelError('');
    tripsApi
      .cancellationQuote(id)
      .then((q) => {
        if (cancelled) return;
        const fee = Number(q.feeAmount);
        const afterPickup =
          q.afterScheduledPickup ||
          (q.hoursNotice != null && q.hoursNotice < 0) ||
          (trip.scheduledDate != null && new Date(trip.scheduledDate).getTime() < Date.now());
        if (!Number.isFinite(fee)) {
          setCancelQuote({ kind: 'error' });
        } else if (fee <= 0 && afterPickup) {
          // Never present a free cancel after pickup — the fee couldn't be worked out.
          setCancelQuote({ kind: 'error' });
        } else if (fee <= 0) {
          setCancelQuote({ kind: 'free' });
        } else {
          setCancelQuote({
            kind: 'fee',
            amount: formatPeso(q.feeAmount),
            reason: feeReason(q.hoursNotice, afterPickup, q.chargePct),
          });
        }
      })
      .catch(() => {
        if (!cancelled) setCancelQuote({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [showCancel, id, trip]);

  const handleCancel = async () => {
    setCancelLoading(true);
    setCancelError('');
    try {
      await tripsApi.cancel(id!);
      setShowCancel(false);
      toast(`Trip ${trip?.reference ?? trip?.tripNumber ?? ''} has been cancelled.`);
      reload();
    } catch {
      setCancelError('We couldn\'t cancel this trip. Please try again or contact your account manager.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleAcceptQuote = async () => {
    setAcceptingQuote(true);
    setQuoteError('');
    try {
      await bookingApi.acceptQuote(id!);
      reload();
    } catch (err) {
      setQuoteError((err as { message?: string }).message ?? 'Failed to accept quote');
    } finally {
      setAcceptingQuote(false);
    }
  };

  const canCancel = canCancelTrip && trip && CLIENT_CANCELLABLE_STATUSES.includes(trip.status);

  const cancelMessage =
    cancelQuote.kind === 'loading'
      ? 'Checking whether a cancellation fee applies…'
      : cancelQuote.kind === 'error'
        ? 'We couldn\'t calculate the fee right now — please try again or contact us.'
        : cancelQuote.kind === 'free'
          ? 'You can cancel this trip free of charge.'
          : `A cancellation fee of ${cancelQuote.amount} applies ${cancelQuote.reason}.`;

  if (loading) {
    return (
      <PortalLayout>
        <div className="loading-center"><div className="spinner" /></div>
      </PortalLayout>
    );
  }

  if (error || !trip) {
    return (
      <PortalLayout>
        <div className="page empty-state">
          <h3>{offline ? 'Couldn\'t load this trip' : 'Trip not found'}</h3>
          {error && offline && <p className="text-muted">{error}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'center' }}>
            {offline && <Button variant="secondary" onClick={() => reload()}>Retry</Button>}
            <Button onClick={() => navigate('/portal')}>Back to trips</Button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  const milestoneStatus = DELIVERED_STATUSES.includes(trip.status)
    ? 'completed'
    : MILESTONE_STATUSES.includes(trip.status)
      ? trip.status
      : 'requested';
  const currentMilestoneIdx = MILESTONE_STATUSES.indexOf(milestoneStatus);
  const tripEnded = ENDED_STATUSES.includes(trip.status);
  const onHold = trip.status === 'on_hold';
  const nextStep = WHAT_HAPPENS_NEXT[trip.status];

  return (
    <PortalLayout>
      <div className="page">
        <div className="portal-nav">
          <PortalNav variant="top" />
        </div>

        <div className="page-header row-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal')}>← Trips</Button>
            <h1>{trip.reference ?? trip.tripNumber}</h1>
            <StatusBadge status={trip.status} audience="client" />
            {nextStep && (
              <p className="text-muted" style={{ marginTop: 'var(--space-xs)', fontSize: '0.875rem' }}>
                {nextStep}
              </p>
            )}
          </div>
          {canCancel && (
            <Button variant="danger" onClick={() => setShowCancel(true)}>Cancel trip</Button>
          )}
        </div>

        {tripEnded || onHold ? (
          <div className={`trip-banner ${onHold ? 'trip-banner-hold' : 'trip-banner-ended'}`} role="status">
            <strong>{clientStatusLabel(trip.status)}</strong> — {nextStep}
          </div>
        ) : (
          <div className="milestone-timeline">
            {MILESTONE_STATUSES.map((status, i) => (
              <div key={status} className={`milestone ${i <= currentMilestoneIdx ? 'done' : ''} ${i === currentMilestoneIdx ? 'current' : ''}`}>
                <div className="milestone-dot" />
                <span className="milestone-label">{MILESTONE_LABELS[status]}</span>
              </div>
            ))}
          </div>
        )}

        {canSeeAmounts && trip.quoteRequired && !trip.quoteAccepted && (
          <section className="detail-section quote-callout" style={{ marginTop: 'var(--space-lg)' }}>
            <h3>Quote required</h3>
            {trip.manualPrice ? (
              <>
                <p style={{ marginBottom: 'var(--space-md)' }}>
                  This trip falls outside our standard coverage area. Your quoted price is{' '}
                  <strong>{formatPeso(trip.manualPrice)}</strong>
                  {trip.manualPriceBasis ? ` (${trip.manualPriceBasis})` : ''}.
                </p>
                {quoteError && <p className="text-urgent" style={{ fontSize: '0.875rem' }}>{quoteError}</p>}
                {hasPermission('portal.book') && (
                  <Button onClick={handleAcceptQuote} loading={acceptingQuote}>Accept quote</Button>
                )}
              </>
            ) : (
              <p className="text-muted">
                This trip falls outside our standard coverage area. We&apos;re preparing a quote for you —
                you&apos;ll be able to accept it here once it&apos;s ready.
              </p>
            )}
          </section>
        )}

        {canSeeAmounts && trip.quoteRequired && trip.quoteAccepted && (
          <section className="detail-section quote-callout" style={{ marginTop: 'var(--space-lg)' }}>
            <h3>Quote accepted</h3>
            <p>
              You accepted the quoted price of <strong>{formatPeso(trip.manualPrice ?? trip.totalCharges)}</strong>.
              We&apos;ll confirm the trip shortly.
            </p>
          </section>
        )}

        <div className="grid-2" style={{ marginTop: 'var(--space-xl)' }}>
          <section className="detail-section">
            <h3>Trip details</h3>
            <dl className="detail-list">
              <dt>Origin</dt><dd>{trip.origin ?? '—'}</dd>
              <dt>Destination</dt><dd>{trip.destination ?? '—'}</dd>
              <dt>Scheduled</dt><dd>{formatDateTime(trip.scheduledDate)}</dd>
              <dt>Cargo</dt><dd>{trip.cargoSummary ?? '—'}</dd>
              {canSeeAmounts && (
                <>
                  <dt>Amount</dt>
                  <dd>{trip.totalCharges ? formatPeso(trip.totalCharges) : 'Pending'}</dd>
                </>
              )}
            </dl>
          </section>

          <section className="detail-section">
            <h3>Stops</h3>
            {trip.stops && trip.stops.length > 0 ? (
              <ol className="stop-list">
                {trip.stops.map((stop: Stop) => (
                  <li key={stop.id} className="stop-item">
                    <div className="stop-seq">{stop.sequence}</div>
                    <div>
                      <div>{stopTypeLabel(stop.type)} · <StatusBadge status={stop.status} size="sm" audience="client" /></div>
                      <div className="text-muted" style={{ fontSize: '0.8125rem' }}>{stop.address}</div>
                      {(stop.scheduledTime || stop.arrivedAt) && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {stop.scheduledTime && <>Scheduled {formatDateTime(stop.scheduledTime)}</>}
                          {stop.scheduledTime && stop.arrivedAt && ' · '}
                          {stop.arrivedAt && <>Arrived {formatDateTime(stop.arrivedAt)}</>}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-muted">Stops will appear once we plan the route.</p>
            )}
          </section>
        </div>

        {pods && pods.length > 0 && (
          <section className="detail-section" style={{ marginTop: 'var(--space-lg)' }}>
            <h3>Proof of delivery</h3>
            <ul className="pod-list">
              {pods.map((pod: PodDocument) => (
                <li key={pod.id}>
                  <span>{pod.podNumber}</span>
                  <span className="pod-meta">
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {formatDate(pod.generatedAt)}
                      {pod.signatureImageKey ? ' · Signed' : ' · Awaiting signature'}
                    </span>
                    {hasPermission('portal.download_docs') && (
                      <Button
                        size="sm"
                        variant="secondary"
                        type="button"
                        loading={downloadingPod === pod.id}
                        onClick={() => void downloadPod(pod)}
                      >
                        Download PDF
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <ConfirmModal
        open={showCancel}
        onClose={() => setShowCancel(false)}
        onConfirm={handleCancel}
        title="Cancel trip?"
        confirmLabel="Cancel trip"
        cancelLabel="Keep trip"
        danger
        loading={cancelLoading}
        confirmDisabled={cancelQuote.kind === 'loading' || cancelQuote.kind === 'error'}
        message={
          <>
            <p>{cancelMessage}</p>
            {cancelError && (
              <p className="text-urgent" role="alert" style={{ marginTop: 'var(--space-sm)', fontSize: '0.875rem' }}>
                {cancelError}
              </p>
            )}
          </>
        }
      />

      <style>{`
        .trip-banner {
          border-radius: var(--radius-md);
          padding: var(--space-md) var(--space-lg);
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text);
        }
        .trip-banner-ended { border-left: 4px solid var(--color-status-cancel); }
        .trip-banner-hold { border-left: 4px solid var(--color-status-hold); }
        .milestone-timeline {
          display: flex;
          gap: 0;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          overflow-x: auto;
        }
        .milestone {
          flex: 1;
          text-align: center;
          position: relative;
          min-width: 80px;
        }
        .milestone-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--color-border);
          margin: 0 auto var(--space-sm);
        }
        .milestone.done .milestone-dot { background: var(--color-forest); }
        .milestone.current .milestone-dot {
          background: var(--color-forest);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-forest) 30%, transparent);
        }
        .milestone-label {
          font-size: 0.6875rem;
          text-transform: uppercase;
          color: var(--color-text-muted);
        }
        .milestone.done .milestone-label { color: var(--color-forest); }
        .detail-section {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .detail-section h3 { margin-bottom: var(--space-md); color: var(--color-forest); }
        .detail-list {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: var(--space-sm) var(--space-md);
          font-size: 0.9375rem;
        }
        .detail-list dt { font-weight: 500; color: var(--color-text-muted); }
        .stop-list { list-style: none; padding: 0; }
        .stop-item {
          display: flex;
          gap: var(--space-md);
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
        }
        .stop-seq {
          width: 24px; height: 24px;
          background: var(--color-forest);
          color: var(--color-sand);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem; font-weight: 600;
        }
        .pod-list { list-style: none; padding: 0; }
        .pod-meta { display: inline-flex; align-items: center; gap: var(--space-sm); flex-wrap: wrap; justify-content: flex-end; }
        .pod-list li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
        }
      `}</style>
    </PortalLayout>
  );
}
